/**
 * Sentiment API - v3.0 AI Sentiment Endpoints
 * Market → Sector → Portfolio sentiment analysis
 */

import { apiRequest } from './client';

// ============================================================================
// Types
// ============================================================================

export interface MarketSentimentResponse {
  overallSentiment: number; // -1.0 to 1.0
  sentimentLabel: 'BULLISH' | 'NEUTRAL' | 'BEARISH';
  breakdown: {
    monetary: { sentiment: number; label: string; context: string };
    geopolitical: { sentiment: number; label: string; context: string };
    economic: { sentiment: number; label: string; context: string };
    market: { sentiment: number; label: string; context: string };
  };
  topNews: Array<{
    title: string;
    category: string;
    sentiment: number;
    impact: string;
  }>;
  timestamp: string;
  cached?: boolean;
  cacheStatus?: 'HIT' | 'MISS' | 'STALE_REFRESH'; // v3.4.5: DO-only cache (v3.3.0+)
  cachedAt?: string;
  missDetails?: 'L1_AND_L2' | 'L1_ONLY';

  // v3.0.30+ Enhanced miss tracking
  lastKnownCachedAt?: string;
  timeSinceLastCache?: number;
  missReason?: 'EXPIRED' | 'NEVER_CACHED' | 'UNKNOWN';
}

export interface SectorSentiment {
  symbol: string;
  name: string;
  sentiment: number; // -1.0 to 1.0
  sentimentLabel: 'BULLISH' | 'NEUTRAL' | 'BEARISH';
  aiContext: string;
  newsCount: number;
}

export interface SectorSentimentResponse {
  sectors: SectorSentiment[];
  timestamp: string;
  cached?: boolean;
  cacheStatus?: 'HIT' | 'MISS' | 'STALE_REFRESH'; // v3.4.5: DO-only cache (v3.3.0+)
  cachedAt?: string;
  missDetails?: 'L1_AND_L2' | 'L1_ONLY';

  // v3.0.30+ Enhanced miss tracking
  lastKnownCachedAt?: string;
  timeSinceLastCache?: number;
  missReason?: 'EXPIRED' | 'NEVER_CACHED' | 'UNKNOWN';
}

export interface MoneyFlowIndicators {
  symbol: string;
  obv: number; // On-Balance Volume (cumulative)
  cmf: number; // Chaikin Money Flow (-1 to +1, typically -0.5 to +0.5)
  trend: 'ACCUMULATION' | 'DISTRIBUTION'; // CMF >= 0 = ACCUMULATION, CMF < 0 = DISTRIBUTION
  timestamp: string;
  cached?: boolean;
  cacheStatus?: 'HIT' | 'MISS';
  cachedAt?: string;
}

export interface StockSentiment {
  symbol: string;
  name?: string;
  price?: number;
  priceChange?: number;
  gptContext: string;
  gptSentiment: number;
  distilbertSentiment: number;
  agreement: 'AGREE' | 'PARTIAL' | 'DISAGREE';
  finalSignal: 'STRONG_BUY' | 'OPPORTUNITY' | 'BULL_TRAP' | 'STRONG_SELL';
  confidence: number; // 0-100
  newsCount: number;
  moneyFlow?: MoneyFlowIndicators | null; // Money flow indicators (OBV/CMF/trend)
}

export interface StockSentimentResponse {
  portfolio: {
    id: string;
    name: string;
  };
  stocks: StockSentiment[];
  timestamp: string;
  cached?: boolean;
  cacheStatus?: 'HIT' | 'MISS' | 'STALE_REFRESH'; // v3.4.5: DO-only cache (v3.3.0+)
  cachedAt?: string;
  missDetails?: 'L1_AND_L2' | 'L1_ONLY';

  // v3.0.30+ Enhanced miss tracking
  lastKnownCachedAt?: string;
  timeSinceLastCache?: number;
  missReason?: 'EXPIRED' | 'NEVER_CACHED' | 'UNKNOWN';
}

// ============================================================================
// API Methods
// ============================================================================

/**
 * Get market-wide sentiment analysis (Tier 1)
 * Analyzes 4 categories: Monetary, Geopolitical, Economic, Market
 */
export async function getMarketSentiment(): Promise<MarketSentimentResponse> {
  return apiRequest<MarketSentimentResponse>('/api/sentiment/market');
}

/**
 * Get sector sentiment analysis (Tier 2)
 * @param sectors - Array of sector symbols (e.g., ['XLK', 'XLE', 'XLF']) or undefined for default (XLK)
 */
export async function getSectorSentiment(
  sectors?: string[]
): Promise<SectorSentimentResponse> {
  const query = sectors && sectors.length > 0
    ? `?sectors=${sectors.join(',')}`
    : '?sectors=default';
  return apiRequest<SectorSentimentResponse>(`/api/sentiment/sectors${query}`);
}

/**
 * Get portfolio stock sentiment analysis (Tier 3)
 * @param portfolioId - Portfolio ID (e.g., 'portfolio_1759694502042_ggxyk8q')
 */
export async function getStockSentiment(
  portfolioId: string
): Promise<StockSentimentResponse> {
  return apiRequest<StockSentimentResponse>(
    `/api/sentiment/stocks?portfolio=${portfolioId}`
  );
}
